"use client";

/**
 * React context that carries the current `AppSurface` for the page.
 *
 * Hydrated from the server-resolved cookie value by `<HostProvider>` in
 * the root layout, so the very first render already knows the surface
 * (no flash of the wrong UI on mount).
 *
 * @see ./host — pure utilities + sessionStorage helpers
 * @see ../components/host-provider — root provider that mounts Office.js
 *      etc. when the surface is an Office add-in
 */

import type { AppSurface } from "@workspace/services/domain";
import { createContext, useContext } from "react";

const HostContext = createContext<AppSurface>("standalone-web");

export const HostContextProvider = HostContext.Provider;

/**
 * Returns the current `AppSurface` for the page.
 *
 * Use the exact value when you want host-specific behaviour (e.g. enable
 * Excel client-tools only when `host === "excel-add-in"`). Use
 * `isOfficeAddin(host)` from `@/lib/host` when you want "any Office host."
 */
export function useHost(): AppSurface {
  return useContext(HostContext);
}
